import { useCallback, useEffect } from 'react'
import {
  deleteSelectedChannels,
  fetchChannels,
  reset,
  setPage,
  setSelectedIds,
  setSize,
} from 'features/channel/list/channelListSlice'
import { batch, useDispatch, useSelector } from 'react-redux'
import { failWithErrorResponse } from 'features/toast/Toast'
import { useDialog } from 'features/dialog/Dialog'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import { useConfirm } from 'features/dialog/Confirm'
import PageHeader from '../../../components/PageHeader'
import { Link, useRouteMatch } from 'react-router-dom'
import Button from '../../../components/form/Button'
import { isEmpty } from '../../../utils'
import Table from '../../../components/table/Table'
import ChannelListItem from './ChannelListItem'
import Pagination from '../../../components/pagination/Pagination'
import PageSize from '../../../components/pagination/PageSize'
import ReviewReasonModal from '../../reviewReason/ReviewReasonModal'

const ChannelList = () => {
  const dispatch = useDispatch()
  const { selectedIds, page, size, total, loadingError, isLoading, channels } =
    useSelector(state => state.channel.channelList)
  const { url } = useRouteMatch()
  const [DeleteFailReason, showDeleteFailReason, hideDeleteFailReason] =
    useDialog()
  const [DeleteBizChannel, showDeleteBizChannel] = useConfirm()

  useEffect(() => {
    dispatch(fetchChannels())
  }, [dispatch, page, size])

  useEffect(() => () => dispatch(reset()), [dispatch])

  const handleSelectionChange = useCallback(
    ids => dispatch(setSelectedIds(ids)),
    [dispatch]
  )

  const handleChannelDelete = useCallback(async () => {
    const { error, payload } = await dispatch(deleteSelectedChannels())
    if (error) {
      if (payload.errorCode === 0) {
        showDeleteFailReason()
      } else {
        failWithErrorResponse(payload)
      }
    } else {
      dispatch(fetchChannels())
    }
  }, [dispatch, showDeleteFailReason])

  const handlePageChange = useCallback(
    v => {
      dispatch(setPage(v))
    },
    [dispatch]
  )

  const handleSizeChange = useCallback(
    v => {
      batch(() => {
        dispatch(setSize(v))
        dispatch(setPage(0))
      })
    },
    [dispatch]
  )

  return (
    <>
      <div className="bizchannel_wrap">
        <div className="dsp_tit">
          <PageHeader pageTitle="비즈채널 관리" />
        </div>
        <div className="ad_managebox">
          <div className="managebox_tit">
            <strong className="tit_box">
              총 비즈채널 관리<span className="fc_emph"> {total}</span>
            </strong>
            <div className="f_left">
              <div className="single_wrap">
                <Link to={`${url}/add`} className="btn_gm gm_bl">
                  <span className="inner_g">
                    <span className="img_comm ico_add" />
                    비즈채널 등록하기
                  </span>
                </Link>
              </div>
              <div className="single_wrap">
                <Button
                  size="medium"
                  line
                  disabled={isEmpty(selectedIds)}
                  onClick={showDeleteBizChannel}>
                  삭제
                </Button>
              </div>
            </div>
          </div>
          <div className="tblg2_wrap">
            <Table
              className="tbl_g2"
              caption="비즈채널 관리 표"
              emptyMessage={
                <>
                  등록된 비즈채널이 없습니다.
                  <br />
                  <Link to={`${url}/add`} className="link_txt">
                    비즈채널 등록하기
                  </Link>
                  를 클릭하여 비즈채널을 등록하세요.
                </>
              }
              error={!!loadingError}
              loading={!!isLoading}
              onSelectionChange={handleSelectionChange}>
              <Table.Head>
                <Table.Column width={45} checkbox />
                <Table.Column title="비즈채널" width={244} />
                <Table.Column title="유형" width={112} />
                <Table.Column title="심사상태" width={100} />
                <Table.Column title="운영상태" width={136} />
                <Table.Column title="카카오톡 채널" width={214} />
                <Table.Column title="카테고리" width={211} />
                <Table.Column title="심사서류" width={110} />
              </Table.Head>
              <Table.Body>
                {channels.map(channel => (
                  <Table.Row key={channel.id} rowId={channel.id}>
                    <ChannelListItem {...channel} />
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          {total > 0 && (
            <div className="paging_wrap">
              <div className="inner_paging">
                <Pagination
                  current={page}
                  total={total}
                  size={size}
                  onChange={handlePageChange}
                />
                <PageSize onSizeChange={handleSizeChange} size={size} />
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteBizChannel
        title="비즈채널 삭제"
        onClose={({ isConfirmed }) => {
          if (isConfirmed) handleChannelDelete()
        }}>
        {selectedIds.length}개의 비즈채널을 정말 삭제 하시겠습니까?
      </DeleteBizChannel>

      <DeleteFailReason className="bizch_layer" type="basic_layer">
        <ConfirmModal
          title="비즈채널 삭제"
          handleClose={hideDeleteFailReason}
          onCancel={null}>
          <p className="subtit_layer">
            사이트 삭제가 불가능합니다.
            <br />
            삭제 희망 사이트 중 한개라도 다음과 같은 경우에 삭제가 불가능합니다.
          </p>
          <ul className="list_guide">
            <li>사이트가 관리자정지 상태일 경우</li>
            <li>사이트와 연결된 그룹이 있을 경우</li>
          </ul>
        </ConfirmModal>
      </DeleteFailReason>
      <ReviewReasonModal />
    </>
  )
}

export default ChannelList
